'use client';

import { useMemo, useCallback } from 'react';
import { useAuth } from '@/lib/auth';

export function useTimezone() {
  const { session } = useAuth();
  
  // Get timezone from session data or fallback to browser timezone
  const userTimezone = useMemo(() => {
    // First try to get from session timezone metadata
    if (session?.timezoneMetadata?.userTimezone) {
      console.log(' [TIMEZONE] Using user preferred timezone from session:', session.timezoneMetadata.userTimezone);
      return session.timezoneMetadata.userTimezone;
    }
    
    // Fallback to browser timezone
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log(' [TIMEZONE] No user preferred timezone found, using browser timezone:', browserTimezone);
    return browserTimezone;
  }, [session?.timezoneMetadata?.userTimezone]);

  // Memoize formatting functions to prevent recreation on every render
  const formatDate = useCallback((date: Date | string, options?: Intl.DateTimeFormatOptions) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone,
      ...options
    };

    try {
      return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
    } catch (error) {
      console.error(' [TIMEZONE] Error formatting date:', error);
      // Fallback to local timezone
      const fallbackOptions = { 
        ...defaultOptions, 
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone 
      };
      return new Intl.DateTimeFormat('en-US', fallbackOptions).format(dateObj);
    }
  }, [userTimezone]);

  const formatDateWithTimezone = useCallback((
    date: Date | string, 
    showTimezone = false, 
    options?: Intl.DateTimeFormatOptions
  ) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      console.warn(' [TIMEZONE] Invalid date provided:', date);
      return 'Invalid date';
    }

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone,
      ...options
    };

    //console.log(' [TIMEZONE] Formatting date:', dateObj.toISOString(), 'with timezone:', userTimezone);

    try {
      const formatted = new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
      
      if (showTimezone) {
        const timezoneAbbr = new Intl.DateTimeFormat('en-US', {
          timeZoneName: 'short',
          timeZone: userTimezone
        }).format(dateObj).split(' ').pop() || '';
        
        const result = `${formatted} (${timezoneAbbr})`;
        //console.log(' [TIMEZONE] Formatted with timezone:', result);
        return result;
      }
      
      //console.log(' [TIMEZONE] Formatted date:', formatted);
      return formatted;
    } catch (error) {
      console.error(' [TIMEZONE] Error formatting date:', error);
      const fallbackOptions = { 
        ...defaultOptions, 
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone 
      };
      const fallbackResult = new Intl.DateTimeFormat('en-US', fallbackOptions).format(dateObj);
      console.log(' [TIMEZONE] Using fallback timezone formatting:', fallbackResult);
      return fallbackResult;
    }
  }, [userTimezone]);

  const getRelativeTimeString = useCallback((date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    // Use UTC timestamps for comparison (both dates are already in UTC)
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years} year${years !== 1 ? 's' : ''} ago`;
    }
  }, []);

  return {
    userTimezone,
    isLoading: false, // No loading state since we get timezone from session
    error: null, // No error state since we always have a fallback
    formatDate,
    formatDateWithTimezone,
    getRelativeTimeString
  };
}